/**
 * @fileoverview Invoices Page - Accounts Receivable
 * @task B.INV.5 - Accounts Receivable invoice list screen
 * @theme Polished Corporate - Teal accents, refined shadows
 *
 * Displays invoice list with filters, summary cards, and data table
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/ui/button';
import { InvoiceStatusBadge } from '@components/invoices/InvoiceStatusBadge';
import { InvoiceSummaryCards, type InvoiceSummaryData } from '@components/invoices/InvoiceSummaryCards';
import { cn } from '@lib/utils';
import { useMarket } from '@contexts/MarketContext';
import { useMarketApi } from '../../hooks/useMarketApi';
import type { Invoice, InvoiceStatus, Customer } from '@lib/demo-data';

// Debounce hook for search input
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function InvoicesPage(): JSX.Element {
  const navigate = useNavigate();
  const { formatCurrency, formatDate, isBrazil, market } = useMarket();
  const { invoicesApi, customersApi } = useMarketApi();

  // State
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [summary, setSummary] = useState<InvoiceSummaryData>({
    totalOutstanding: 0,
    expectedThisMonth: 0,
    overdue: 0,
    collectedThisMonth: 0,
    overdueCount: 0,
    expectedCount: 0,
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const result = await invoicesApi.getInvoices({
        ...(status ? { status } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        page,
        limit: pageSize,
      });
      setInvoices(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.total);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, debouncedSearch, page, pageSize, invoicesApi]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    setIsSummaryLoading(true);
    try {
      const summaryData = await invoicesApi.getInvoicesSummary();
      setSummary(summaryData);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setIsSummaryLoading(false);
    }
  }, [invoicesApi]);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const customersResult = await customersApi.getCustomers();
      setCustomers(customersResult.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [customersApi]);

  // Initial load - re-fetch when market changes
  useEffect(() => {
    void fetchCustomers();
    void fetchSummary();
  }, [fetchCustomers, fetchSummary, market]);

  // Load invoices when filters change
  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, customerFilter, debouncedSearch]);

  const handleCreateInvoice = (): void => {
    navigate('/accounts-receivable/invoices/new');
  };

  const handleViewInvoice = (id: string): void => {
    navigate(`/accounts-receivable/invoices/${id}`);
  };

  const statusOptions: { value: InvoiceStatus | 'all'; label: string }[] = isBrazil
    ? [
        { value: 'all', label: 'Todos os Status' },
        { value: 'draft', label: 'Rascunho' },
        { value: 'sent', label: 'Enviado' },
        { value: 'viewed', label: 'Visualizado' },
        { value: 'partial', label: 'Parcial' },
        { value: 'paid', label: 'Pago' },
        { value: 'overdue', label: 'Vencido' },
        { value: 'void', label: 'Cancelado' },
      ]
    : [
        { value: 'all', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'viewed', label: 'Viewed' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'void', label: 'Void' },
      ];

  return (
    <div className="min-h-screen bg-gray-50/30">
      <main className="container py-6">
        {/* Action Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm sm:text-base text-gray-500">
            {isBrazil ? 'Gerenciar NF-es e pagamentos de clientes' : 'Manage customer invoices and payments'}
          </p>
          <Button
            onClick={handleCreateInvoice}
            className="gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm transition-all hover:from-teal-600 hover:to-teal-700 hover:shadow-md min-h-[44px] w-full sm:w-auto"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {isBrazil ? 'Criar NF-e' : 'Create Invoice'}
          </Button>
        </div>
        {/* Summary Cards */}
        <div className="mb-6">
          <InvoiceSummaryCards data={summary} isLoading={isSummaryLoading} isBrazil={isBrazil} formatCurrency={formatCurrency} />
        </div>

        {/* Filters - Mobile optimized with stacking */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 rounded-xl bg-white p-3 sm:p-4 shadow-card">
          {/* Search */}
          <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={isBrazil ? 'Buscar NF-es...' : 'Search invoices...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-10 pr-4 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            />
          </div>

          {/* Filter row on mobile */}
          <div className="flex gap-2 sm:contents">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
              className="h-10 flex-1 sm:flex-none rounded-xl border border-gray-200 bg-gray-50/50 px-2 sm:px-3 text-xs sm:text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Customer Filter */}
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="h-10 flex-1 sm:flex-none rounded-xl border border-gray-200 bg-gray-50/50 px-2 sm:px-3 text-xs sm:text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 sm:min-w-[180px]"
            >
              <option value="">{isBrazil ? 'Clientes' : 'Customers'}</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name.length > 20 ? `${customer.name.substring(0, 20)}...` : customer.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {(statusFilter !== 'all' || customerFilter || searchQuery) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setCustomerFilter('');
                setSearchQuery('');
              }}
              className="text-gray-500 hover:text-teal-600 hover:bg-teal-50 min-h-[40px] w-full sm:w-auto"
            >
              <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {isBrazil ? 'Limpar filtros' : 'Clear filters'}
            </Button>
          )}
        </div>

        {/* Data Table - Mobile optimized */}
        <div className="overflow-hidden rounded-xl bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {isBrazil ? 'NF-e #' : 'Invoice #'}
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {isBrazil ? 'Cliente' : 'Customer'}
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {isBrazil ? 'Valor' : 'Amount'}
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    {isBrazil ? 'Emissão' : 'Issue'}
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    {isBrazil ? 'Venc.' : 'Due'}
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {isBrazil ? 'Ações' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  // Loading skeleton
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td className="px-4 py-4">
                        <div className="h-4 w-24 rounded-lg bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-32 rounded-lg bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="ml-auto h-4 w-20 rounded-lg bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-20 rounded-lg bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-20 rounded-lg bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-5 w-16 rounded-full bg-gray-100" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="ml-auto h-8 w-20 rounded-lg bg-gray-100" />
                      </td>
                    </tr>
                  ))
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="mt-2 text-sm font-medium text-gray-600">
                        {isBrazil ? 'Nenhuma NF-e encontrada' : 'No invoices found'}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {isBrazil ? 'Tente ajustar os filtros de busca' : 'Try adjusting your search or filter criteria'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => {
                    // Truncate customer name for mobile
                    const displayName = invoice.customerName.length > 18 ? `${invoice.customerName.substring(0, 18)}...` : invoice.customerName;
                    return (
                      <tr
                        key={invoice.id}
                        className="group transition-colors cursor-pointer hover:bg-teal-50/50"
                        onClick={() => handleViewInvoice(invoice.id)}
                      >
                        <td className="px-2 sm:px-4 py-3 sm:py-4">
                          <span className="font-mono text-xs sm:text-sm font-semibold text-teal-600">
                            {invoice.invoiceNumber}
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4">
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[100px] sm:max-w-none" title={invoice.customerName}>
                              <span className="sm:hidden">{displayName}</span>
                              <span className="hidden sm:inline">{invoice.customerName}</span>
                            </p>
                            <p className="text-[10px] sm:text-xs text-gray-400">
                              {invoice.lineItems.length} {isBrazil ? (invoice.lineItems.length > 1 ? 'itens' : 'item') : 'item(s)'}
                            </p>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4 text-right">
                          <div>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(invoice.amount)}</p>
                            {invoice.paidAmount > 0 && invoice.paidAmount < invoice.amount && (
                              <p className="text-[10px] sm:text-xs text-emerald-600 whitespace-nowrap">
                                {isBrazil ? 'Pago' : 'Paid'}: {formatCurrency(invoice.paidAmount)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 hidden md:table-cell whitespace-nowrap">
                          {formatDate(invoice.issueDate)}
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4 hidden sm:table-cell">
                          <span
                            className={cn(
                              'text-xs sm:text-sm whitespace-nowrap',
                              invoice.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'
                            )}
                          >
                            {formatDate(invoice.dueDate)}
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4">
                          <InvoiceStatusBadge status={invoice.status} isBrazil={isBrazil} />
                        </td>
                        <td className="px-2 sm:px-4 py-3 sm:py-4 text-right">
                          <div className="flex items-center justify-end gap-1 sm:gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInvoice(invoice.id);
                              }}
                              className="text-gray-600 hover:text-teal-600 hover:bg-teal-50 text-xs sm:text-sm px-2 sm:px-3 min-h-[36px]"
                            >
                              {isBrazil ? 'Ver' : 'View'}
                            </Button>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="p-2 sm:p-2.5 min-h-[36px] min-w-[36px] rounded-xl text-gray-400 transition-all hover:bg-teal-50 hover:text-teal-600 flex items-center justify-center"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - Mobile optimized */}
          {totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100 px-3 sm:px-4 py-3">
              <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                <span className="sm:hidden">
                  {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)}/{totalCount}
                </span>
                <span className="hidden sm:inline">
                  {isBrazil
                    ? `Mostrando ${((page - 1) * pageSize) + 1} a ${Math.min(page * pageSize, totalCount)} de ${totalCount} NF-es`
                    : `Showing ${((page - 1) * pageSize) + 1} to ${Math.min(page * pageSize, totalCount)} of ${totalCount} invoices`
                  }
                </span>
              </p>
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-xl border-gray-200 text-gray-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 min-h-[40px] min-w-[40px] sm:min-w-0 p-2 sm:px-3"
                >
                  <span className="hidden sm:inline">{isBrazil ? 'Anterior' : 'Previous'}</span>
                  <svg className="h-4 w-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .map((p, index, arr) => {
                      const prevItem = arr[index - 1];
                      const showEllipsis = index > 0 && prevItem !== undefined && p - prevItem > 1;
                      return (
                        <span key={p}>
                          {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                          <Button
                            variant={p === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPage(p)}
                            className={cn(
                              'min-w-[36px] rounded-xl',
                              p === page
                                ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm'
                                : 'border-gray-200 text-gray-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200'
                            )}
                          >
                            {p}
                          </Button>
                        </span>
                      );
                    })}
                </div>
                {/* Mobile: show current/total */}
                <span className="sm:hidden px-3 text-sm font-medium text-gray-700">
                  {page}/{totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="rounded-xl border-gray-200 text-gray-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 min-h-[40px] min-w-[40px] sm:min-w-0 p-2 sm:px-3"
                >
                  <span className="hidden sm:inline">{isBrazil ? 'Próximo' : 'Next'}</span>
                  <svg className="h-4 w-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
